import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import type {
  Annotation,
  DrawStroke,
  Row,
  ScriptElement,
  Slide,
  Spotlight,
} from '../schema';
import { StageElement } from './StageElement';
import type { McqInteractive } from './elements/McqElement';
import { AnnotationOverlay, type AnnotationRect } from './AnnotationOverlay';
import { strokePointsAttr } from '../engine/draw-playback';
import { StrokeTextureFilter, strokeFilterId } from './stroke-texture';
import { AUTO_SIZE_TYPES, CANVAS_WIDTH, SLIDE_REF_HEIGHT, styleOverrides } from './boardLayout';

/**
 * The chalkboard rendering surface for V2 packs.
 *
 * Renders the slide EXACTLY as authored on the backend — mirroring the admin's
 * `teach/Stage.tsx`: rows[].columns[].elements[] with freeform absolute
 * positioning for `layout === 0` and flex-column stacks for `layout 1|2|3`.
 * Authored positions (x/y/w/h) and styles (font size, image size) are honored
 * verbatim; nothing is re-flowed or overridden.
 *
 * Positions are authored against a fixed {@link CANVAS_WIDTH}×{@link
 * SLIDE_REF_HEIGHT} canvas. We render at that native size and then CSS-scale
 * the whole canvas up to fill the available landscape width, so the slide
 * spans the full board without distorting any authored coordinate.
 */

/**
 * Scroll the nearest scrollable ancestor so `node` (just-written content) stays
 * in view — used to auto-follow the lecture as the board fills up.
 */
function followIntoView(node: HTMLElement) {
  let el: HTMLElement | null = node.parentElement;
  while (el) {
    const oy = getComputedStyle(el).overflowY;
    if (
      (oy === 'auto' || oy === 'scroll') &&
      el.scrollHeight > el.clientHeight + 1
    )
      break;
    el = el.parentElement;
  }
  if (!el) return;
  const cRect = el.getBoundingClientRect();
  const nRect = node.getBoundingClientRect();
  // Keep the active line well clear of the bottom — enough to sit above the
  // floating player bar so the newest writing is never hidden behind it.
  const margin = 140;
  if (nRect.bottom > cRect.bottom - margin) {
    el.scrollTo({
      top: el.scrollTop + (nRect.bottom - cRect.bottom) + margin,
      behavior: 'smooth',
    });
  } else if (nRect.top < cRect.top + margin) {
    el.scrollTo({
      top: el.scrollTop - (cRect.top - nRect.top) - margin,
      behavior: 'smooth',
    });
  }
}

/** True for any plain-white background (#fff / #ffffff / white / rgb(255,…)).
 *  White slides keep the themed ruled-paper surface instead of a flat white. */
function isWhiteBackground(bg: string | undefined): boolean {
  if (!bg) return false;
  const v = bg.trim().toLowerCase().replace(/\s+/g, '');
  return (
    v === '#fff' ||
    v === '#ffffff' ||
    v === 'white' ||
    v === 'rgb(255,255,255)' ||
    v === 'rgba(255,255,255,1)'
  );
}

interface Props {
  slide: Slide;
  /** Element ids currently revealed. `null` = show everything (preview mode). */
  revealedElementIds: Set<string> | null;
  /** Accumulated annotations from played-so-far revealSteps on this slide. */
  annotations: Annotation[];
  /** When set, mcq elements render as the interactive answer surface. */
  quiz?: McqInteractive;
  /** Suppress the board MCQ (answered in the bottom overlay instead). */
  hideMcq?: boolean;
  /** Freeze the handwriting stream in place (mirrors the player's pause). */
  paused?: boolean;
  /**
   * Active spotlight cue: zoom/pan the board onto its `targetElementId` while
   * set, animate back to 1× when cleared. `null`/non-spotlight = no zoom.
   */
  spotlight?: Annotation | null;
  /**
   * Visible pen/marker strokes (design-px points). The caller controls
   * accumulation/timing (e.g. via `selectVisibleStrokes`); each stroke draws on
   * (traces start→end) when it mounts.
   */
  drawStrokes?: DrawStroke[];
  /**
   * Visible region spotlights (the rich circle/oval zoom + vignette). The first
   * one zooms + centres its region and caves a soft vignette in; when it clears
   * the zoom-out eases with the same duration. Takes precedence over the legacy
   * annotation-kind `spotlight` transform.
   */
  spotlights?: Spotlight[];
  /**
   * Show ONE canvas (row) at a time, zoomed to fill the host box, instead of
   * stacking every canvas into one tall scrolling board. The view swaps to a
   * canvas the moment anything on it is revealed. Used by the admin Preview so
   * a multi-canvas slide is never seen as half of one canvas plus half of the
   * next; the Electron classroom leaves it off and keeps the scrolling feed.
   */
  focusCanvas?: boolean;
  /**
   * Height (px) the focused canvas is fitted into. Required for `focusCanvas`
   * to zoom correctly — the stage cannot read its own available height, since
   * it sets that height itself.
   */
  focusHeight?: number;
  /**
   * On a slide with NO reveal steps the board shows everything at once, so the
   * "this just appeared" transition never fires and handwritten text is simply
   * there, already drawn — no pencil. Hosts that want the writing animation
   * anyway (Preview) set this to treat slide entry as one big reveal. The
   * Electron classroom leaves it off: it re-shows earlier slides in its feed and
   * they must not re-animate.
   */
  animateOnShowAll?: boolean;
  /**
   * Host-controlled canvas index, overriding the automatic pager (the preview's
   * ‹ › buttons). `null`/undefined leaves the pager in charge.
   */
  canvasIndex?: number | null;
  /** Reports the canvas being shown and how many there are, for host controls. */
  onCanvasChange?: (info: { index: number; total: number }) => void;
}

/** A canvas's design-px band height: its authored % of the 720 reference, with
 *  the same 120px floor the admin canvas uses. */
function rowBandHeight(row: Row): number {
  return Math.max(120, (row.heightPercent / 100) * SLIDE_REF_HEIGHT);
}

/** Default zoom factor when a spotlight omits `zoomScale` (integration guide). */
const DEFAULT_SPOTLIGHT_ZOOM = 1.5;
/** Corner radius (px) of the `zoom` hole / shadow ring. */
const SPOTLIGHT_HOLE_RADIUS = 18;
/** Breathing room (px) between the element and the `zoom` hole edge. */
const SPOTLIGHT_HOLE_PAD = 8;

// Soft radial "stage spotlight" used by the dim-only `spotlight` kind: a bright
// clear core over the element fading smoothly to a dark vignette at the screen
// edges (no hard hole, no blur).
/** Px added to the element half-size before the soft falloff scale. */
const SPOTLIGHT_SOFT_PAD = 40;
/** Radius multiplier controlling how far the soft falloff reaches. */
const SPOTLIGHT_SOFT_SCALE = 1.3;
/** % of the radius kept fully clear before the fade begins. */
const SPOTLIGHT_CORE_PCT = 42;
/** Darkness at the vignette edges. */
const SPOTLIGHT_EDGE_DIM = 'rgba(6, 8, 16, 0.6)';
/** Subtle whole-board push-in applied by the `spotlight` kind (1 = none). */
const SPOTLIGHT_PUSH_IN = 1.06;
/**
 * How long the `spotlight` dim takes to close in from the full screen down to
 * the target box (ms). The bright pool starts covering everything and shrinks
 * toward the element, so the darkness grows inward from the corners.
 */
const SPOTLIGHT_CLOSE_MS = 900;

export function SlideStage({
  slide,
  revealedElementIds,
  annotations,
  quiz,
  hideMcq = false,
  paused = false,
  spotlight = null,
  drawStrokes = [],
  spotlights = [],
  focusCanvas = false,
  focusHeight = 0,
  animateOnShowAll = false,
  canvasIndex = null,
  onCanvasChange,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  // Full-window dim+blur overlay (portaled to <body>): `dimRef` is the blurred
  // layer with a rounded-rect hole cut around the spotlighted element via an SVG
  // mask; `ringRef` adds the drop shadow / edge at the hole. Both are positioned
  // imperatively each frame so they track the element through the zoom.
  const dimRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const elementRefs = useRef<Map<string, HTMLElement>>(new Map());
  const [boxes, setBoxes] = useState<Record<string, AnnotationRect>>({});

  // The native canvas (CANVAS_WIDTH wide) is CSS-scaled to fill the landscape.
  // `scale` fills the width; `scaledHeight` reserves the post-scale height so
  // the parent scroll area sees the correct content size.
  const [scale, setScale] = useState(1);
  const [scaledHeight, setScaledHeight] = useState(SLIDE_REF_HEIGHT);
  const [containerW, setContainerW] = useState(0);

  // Which canvas each element lives on, and each canvas's design-px band height.
  // Both drive focus mode; cheap to keep for every host.
  const rowIndexOf = useMemo(() => {
    const m = new Map<string, number>();
    slide.rows.forEach((r, i) =>
      r.columns.forEach((c) => c.elements.forEach((e) => m.set(e.id, i))),
    );
    return m;
  }, [slide]);
  const rowHeights = useMemo(() => slide.rows.map(rowBandHeight), [slide]);
  // Element ids per canvas, in canvas order — the pager walks this.
  const rowsOfIds = useMemo(
    () => slide.rows.map((r) => r.columns.flatMap((c) => c.elements.map((e) => e.id))),
    [slide],
  );
  const lastRow = Math.max(0, slide.rows.length - 1);
  // Which canvas is displayed is entirely the host's call — the preview drives it
  // from its ‹ › buttons. There is deliberately no automatic advance: every rule
  // for guessing when a canvas was "done" (settle beat, empty-canvas wait, stall
  // timeout) produced a regression — first "only the last canvas shows", then
  // "one second then blank". Removing the guessing removes the whole class.
  const focusRow = Math.min(Math.max(0, canvasIndex ?? 0), lastRow);
  const focusBandH = rowHeights[focusRow] ?? SLIDE_REF_HEIGHT;

  /** Every element id named by any reveal step on this slide. */
  const scriptedIds = useMemo(() => {
    const s = new Set<string>();
    (slide.revealSteps ?? []).forEach((st) => (st.elementIds ?? []).forEach((id) => s.add(id)));
    return s;
  }, [slide]);

  /**
   * What the board should be showing right now. `null` = show everything.
   * Two adjustments on top of the engine's set:
   *  - a slide with NO steps shows everything at once, so `animateOnShowAll`
   *    hosts materialise it as a real reveal and get the pencil;
   *  - elements named by no step would otherwise stay invisible for the whole
   *    slide, so a canvas's leftovers are released once its scripted work is
   *    done — but never for a canvas the pager has not reached yet, or they
   *    would count as "content" on a later canvas and drag the view forward.
   */
  const revealedNow = useMemo<Set<string> | null>(() => {
    if (!revealedElementIds) {
      if (animateOnShowAll && scriptedIds.size === 0) return new Set(rowIndexOf.keys());
      // "Nothing revealed" arrives as null, which the board reads as SHOW ALL.
      // On a slide that HAS a script that is wrong twice over: every element
      // flashes on before its cue, and because nothing is in `writingIds` yet
      // they render through the settled path — fully inked, no pencil. That is
      // the "whole text block appeared already written" report. It is a real
      // window, not a single frame: the engine empties the revealed set on
      // every slide change, so it recurs at each slide. A scripted slide with
      // nothing revealed must show NOTHING. Scoped to the preview (focusCanvas)
      // to keep the classroom's feed behaviour exactly as it is.
      if (focusCanvas && scriptedIds.size > 0) return new Set<string>();
      return null;
    }
    const extra: string[] = [];
    rowsOfIds.forEach((ids, i) => {
      if (focusCanvas && i > focusRow) return;
      if (ids.some((id) => scriptedIds.has(id) && !revealedElementIds.has(id))) return;
      ids.forEach((id) => {
        if (!scriptedIds.has(id) && !revealedElementIds.has(id)) extra.push(id);
      });
    });
    return extra.length ? new Set([...revealedElementIds, ...extra]) : revealedElementIds;
  }, [revealedElementIds, animateOnShowAll, scriptedIds, rowIndexOf, rowsOfIds, focusCanvas, focusRow]);


  // Fit the native canvas to the container's width (full-landscape coverage).
  // `scrollHeight` is unaffected by the CSS transform, so it's the true native
  // content height; multiplying by scale gives the rendered height to reserve.
  //
  // In focus mode we instead fit ONE canvas into the host's box on BOTH axes, so
  // a short canvas zooms in and a tall one zooms out — either way exactly one
  // canvas fills the view and no neighbour is ever half-visible.
  const fit = useCallback(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;
    setContainerW(container.clientWidth);
    if (focusCanvas) {
      const boxH = focusHeight || container.clientHeight || SLIDE_REF_HEIGHT;
      setScale(Math.min(container.clientWidth / CANVAS_WIDTH, boxH / focusBandH));
      setScaledHeight(boxH);
      return;
    }
    const s = container.clientWidth / CANVAS_WIDTH;
    setScale(s);
    setScaledHeight(canvas.scrollHeight * s);
  }, [focusCanvas, focusHeight, focusBandH]);

  // Measure each element's box relative to the stage so annotations can overlay
  // precisely. The element rects are already in scaled/positioned space (the
  // overlay renders in the same container), so no scale math is needed here.
  const measure = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const cr = container.getBoundingClientRect();
    // The viewport may be spotlight-zoomed. Measure the element rects relative to
    // the container, then divide out the viewport's *live* transform matrix so
    // `boxes` stays in the un-zoomed coordinate space the overlay/zoom-math use.
    // Reading the computed matrix (not our state) makes this exact even mid-
    // transition, and avoids a measure→zoom→measure feedback loop.
    let z = 1;
    let tx = 0;
    let ty = 0;
    const vp = viewportRef.current;
    const tr = vp ? getComputedStyle(vp).transform : 'none';
    if (tr && tr !== 'none') {
      const m = new DOMMatrixReadOnly(tr);
      z = m.a || 1;
      tx = m.e || 0;
      ty = m.f || 0;
    }
    const next: Record<string, AnnotationRect> = {};
    elementRefs.current.forEach((node, id) => {
      const r = node.getBoundingClientRect();
      next[id] = {
        left: (r.left - cr.left - tx) / z,
        top: (r.top - cr.top - ty) / z,
        width: r.width / z,
        height: r.height / z,
      };
    });
    setBoxes(next);
  }, []);

  const remeasure = useCallback(() => {
    fit();
    measure();
  }, [fit, measure]);

  useEffect(() => {
    remeasure();
    const ro = new ResizeObserver(() => remeasure());
    if (containerRef.current) ro.observe(containerRef.current);
    if (canvasRef.current) ro.observe(canvasRef.current);
    elementRefs.current.forEach((node) => ro.observe(node));
    window.addEventListener('resize', remeasure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', remeasure);
    };
  }, [remeasure, slide]);

  // Re-measure whenever the set of revealed elements changes — newly-revealed
  // elements transition in (opacity 0 → 1) so the initial measurement may
  // catch them at zero size.
  useEffect(() => {
    const id = window.setTimeout(remeasure, 350);
    return () => window.clearTimeout(id);
  }, [revealedElementIds, remeasure]);

  // NOTE: `ref={registerElement(id)}` builds a new closure every render, so React
  // detaches (calls with null) and reattaches on EVERY re-render — not just on
  // unmount. Keep this a pure map write: anything with side effects here (e.g.
  // touching writing state) fires constantly and thrashes the board.
  const registerElement = useCallback(
    (id: string) => (node: HTMLElement | null) => {
      if (node) elementRefs.current.set(id, node);
      else elementRefs.current.delete(id);
    },
    [],
  );

  // ─── "Writing on the board" reveal ────────────────────────────────────────
  // Track which elements *just* became revealed so the pencil-write animation
  // plays only on that transition. useLayoutEffect applies the class before
  // paint, so the element never flashes fully-drawn before the wipe starts.
  const prevRevealedRef = useRef<Set<string>>(new Set());
  const slideIdRef = useRef(slide.id);
  const [writingIds, setWritingIds] = useState<Set<string>>(new Set());

  // Handwritten text (`handwritten !== false` — the flag defaults ON) clears
  // its writing state itself, precisely when its character stream completes
  // (via onWritingDone). Everything else — including text with handwriting
  // UNticked, which just fades in like an image — is cleared by the timed wipe.
  const selfClearingIds = useMemo(() => {
    const s = new Set<string>();
    slide.rows.forEach((r) =>
      r.columns.forEach((c) =>
        c.elements.forEach((e) => {
          if (e.type === 'text' && e.handwritten !== false) s.add(e.id);
          // Latex writes itself on glyph-by-glyph (pencil reveal) — it clears
          // its own writing state when the stream completes, like text.
          if (e.type === 'latex') s.add(e.id);
        }),
      ),
    );
    return s;
  }, [slide]);

  // Per-element WRITE DURATION, synced to the teacher's pace (user 2026-08-13):
  // the slide starts blank and every element reveals when the audio reaches it,
  // so the time it should take to write is the gap until the NEXT element is
  // cued — the pencil finishes just as the next thing appears. Clamped so a long
  // dwell doesn't crawl and a rapid-fire burst doesn't blur. Passed to the
  // writers (`targetMs`), which scale their glyph timing to fill it.
  const writeDurations = useMemo(() => {
    const trig: Array<{ id: string; t: number }> = [];
    const stored: Record<string, number> = {};
    (slide.revealSteps ?? []).forEach((st) => {
      const et = (st as { elementTimings?: Record<string, { triggerMs?: number; writeMs?: number }> }).elementTimings ?? {};
      for (const [id, t] of Object.entries(et)) {
        if (typeof t?.triggerMs === 'number') trig.push({ id, t: t.triggerMs });
        if (typeof t?.writeMs === 'number') stored[id] = t.writeMs;
      }
    });
    trig.sort((a, b) => a.t - b.t);
    const m: Record<string, number> = {};
    for (let i = 0; i < trig.length; i++) {
      const id = trig[i].id;
      if (typeof stored[id] === 'number') {
        // Preferred: the pen fills the time the line is actually SPOKEN, so the
        // handwriting pace tracks the teacher's speaking pace (slow line → slow pen).
        m[id] = Math.max(500, Math.min(stored[id], 30000));
      } else {
        // Fallback (no spoken span): fill the gap until the next element is cued.
        const next = i + 1 < trig.length ? trig[i + 1].t : trig[i].t + 4000;
        m[id] = Math.max(900, Math.min(next - trig[i].t, 12000));
      }
    }
    return m;
  }, [slide]);

  const clearWriting = useCallback((id: string) => {
    setWritingIds((s) => {
      if (!s.has(id)) return s;
      const n = new Set(s);
      n.delete(id);
      return n;
    });
  }, []);

  useLayoutEffect(() => {
    // On slide change, reset the baseline so the new slide's first reveal
    // animates from scratch. (Done here — not in a separate effect — so it
    // can't clobber the writingIds we set below within the same commit.)
    if (slideIdRef.current !== slide.id) {
      slideIdRef.current = slide.id;
      prevRevealedRef.current = new Set();
      // The outgoing slide's elements unmount without ever reporting done —
      // left behind they'd sit in `writingIds` forever.
      setWritingIds(new Set());
    }
    // No reveal steps → the board shows everything at once. `animateOnShowAll`
    // hosts still want the pencil, so treat slide entry as a single reveal of
    // the whole slide; `prevRevealedRef` makes that happen exactly once.
    //
    // `revealedNow` already applies that gate (only a slide with NO steps is
    // materialised) plus the withheld-unscripted rule.
    const effectiveRevealed = revealedNow;
    if (!effectiveRevealed) {
      prevRevealedRef.current = new Set();
      return undefined;
    }
    const prev = prevRevealedRef.current;
    const newly = [...effectiveRevealed].filter((id) => !prev.has(id));
    prevRevealedRef.current = new Set(effectiveRevealed);
    if (newly.length === 0) return undefined;
    // Every newly-cued element writes itself on (the slide starts blank, so
    // there is never a pencil mid-stroke at t=0 to avoid — user 2026-08-13).
    setWritingIds((s) => new Set([...s, ...newly]));

    // Auto-follow for SCROLLING hosts (the Electron classroom): keep the
    // bottom-most freshly-revealed element in view. Deliberately immediate —
    // deferring it until the board fell quiet parked the view on one line for
    // the 10-20s a handwritten stream takes, and deadlocked outright if any
    // element never reported done.
    const raf = window.requestAnimationFrame(() => {
      if (focusCanvas) return;
      let target: HTMLElement | null = null;
      let maxBottom = -Infinity;
      newly.forEach((id) => {
        const n = elementRefs.current.get(id);
        if (!n) return;
        const b = n.getBoundingClientRect().bottom;
        if (b > maxBottom) {
          maxBottom = b;
          target = n;
        }
      });
      if (target) followIntoView(target);
    });

    // Fading elements use the timed wipe; clear them after the wipe plays.
    // Handwritten text clears itself when its character stream finishes
    // (onWritingDone) — excluded here, else a long stream is cut off mid-write.
    const t = window.setTimeout(() => {
      setWritingIds((s) => {
        const n = new Set(s);
        newly.forEach((id) => {
          if (!selfClearingIds.has(id)) n.delete(id);
        });
        return n;
      });
    }, 1300);
    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(t);
    };
  }, [revealedNow, slide, selfClearingIds, focusCanvas]);

  // Let the host render "‹ canvas 2 of 3 ›".
  useEffect(() => {
    onCanvasChange?.({ index: focusRow, total: slide.rows.length });
  }, [focusRow, slide.rows.length, onCanvasChange]);

  // Inner element body: the renderer + the reveal/writing animation. The
  // positioning wrapper (absolute box or flex item) is added by the caller.
  const renderBody = (el: ScriptElement, fill: boolean, visible = true) => {
    // "Writing" must mean writing ON SCREEN. An unrevealed element is still
    // MOUNTED (it is only hidden with opacity:0, which does not collapse
    // layout), so a handwritten element seeded into `writingIds` before its cue
    // used to burn through its whole stream invisibly and then pop into view
    // part-drawn — or fully drawn — depending purely on whether its cue landed
    // before or after its own ink finished. That is what made the pencil play
    // "only in some scenarios". Gating on `visible` also gives a real
    // false→true edge at the cue, which swaps the rendered component and
    // therefore replays the stroke from the first glyph.
    const writing = visible && writingIds.has(el.id);

    // HANDWRITTEN text streams in character-by-character with a pencil hovering
    // over the active character (handled inside the text renderer). Text with
    // handwriting unticked (`handwritten: false`) falls through to the generic
    // fade path and keeps its authored font.
    if ((el.type === 'text' && el.handwritten !== false) || el.type === 'latex') {
      return (
        // Keyed on visibility so becoming visible REMOUNTS the writer and it
        // replays from the first glyph. Gating `writing` on `visible` alone is
        // not enough: the engine reveals a step's whole element list in one
        // commit before word-timing narrows it back down, so every element on
        // the step is briefly visible-and-writing at once. They then keep
        // inking behind opacity:0 and, with `writing` already true, nothing
        // remounts them at their real cue — so they appear already-written.
        // That is why only the first line or two of a slide showed a pencil.
        <div key={visible ? 'shown' : 'hidden'} style={fill ? { height: '100%' } : undefined}>
          <StageElement
            element={el}
            quiz={quiz}
            fill={fill}
            writing={writing}
            writeMs={writeDurations[el.id]}
            onWritingDone={() => clearWriting(el.id)}
            paused={paused}
            hideMcq={hideMcq}
          />
        </div>
      );
    }

    // Non-text elements (images, icons, callouts, mermaid, …) just fade in via
    // the wrapper's opacity transition — no chalk wipe or pencil (the pencil
    // animation belongs to handwritten text only). Callouts additionally swing
    // in like a hanging sign on reveal (paired with the swoosh cue).
    const swing = el.type === 'callout' && writing;
    return (
      <div className={swing ? 'callout-swing' : undefined} style={fill ? { height: '100%' } : undefined}>
        <StageElement element={el} quiz={quiz} fill={fill} hideMcq={hideMcq} />
      </div>
    );
  };

  // One row, rendered EXACTLY like the admin editor: every column is a freeform
  // absolutely-positioned canvas (INTEGRATION §3.1 / BLUEPRINT §13). `layout` is
  // just the column count — columns sit side by side at their `widthPercent`,
  // sharing the row height; each element is placed by its `position`
  // {x,y,w,h,rotation,zIndex}. Elements without a position use the deterministic
  // cascade fallback {x:32, y:24+i*132, w:320, h:120} (same as the editor).
  const renderRow = (row: Row) => {
    const visibleOf = (el: ScriptElement) => revealedNow === null || revealedNow.has(el.id);

    // Row band height = heightPercent of the 720 design canvas. Absolutely-
    // positioned children need an explicit height to lay against.
    const heightPx = rowBandHeight(row);

    return (
      <div
        key={row.id}
        // `gap-3` = the 12px separator BOTH admin renderers put between adjacent
        // columns (teach/Stage.tsx uses `gap-3`; the canvas lays a 12px `w-3`
        // divider between them). Packing columns edge to edge here pushed every
        // column after the first leftwards relative to the canvas.
        className="relative flex w-full gap-3"
        style={{
          height: heightPx,
          paddingTop: row.padding.top,
          paddingRight: row.padding.right,
          paddingBottom: row.padding.bottom,
          paddingLeft: row.padding.left,
        }}
      >
        {row.columns.map((col) => (
          <div
            key={col.id}
            // Freeform canvas: side-by-side at widthPercent, full row height,
            // children positioned absolutely within it. `overflow-hidden`
            // matches BOTH admin renderers (FreeformRow.tsx and teach's column),
            // which clip a canvas's contents to the canvas — without it an
            // element that overflows its canvas is cut on the authoring surface
            // but spills into the neighbouring canvas here.
            className="relative h-full overflow-hidden"
            style={{
              flexBasis: `${col.widthPercent}%`,
              flexGrow: 0,
              flexShrink: 0,
            }}
          >
            {col.elements.map((el, i) => {
              const visible = visibleOf(el);
              const p = el.position;
              const x = p?.x ?? 32;
              const y = p?.y ?? 24 + i * 132;
              const w = p?.w ?? 320;
              const h = p?.h ?? 120;
              const rotation = p?.rotation ?? 0;
              const z = p?.zIndex ?? 1;
              // Chips and icons hug their own content on the authoring canvas —
              // their stored w/h is leftover and usually too small, so forcing
              // it here sliced the pill. Ported from the admin teach stage,
              // which already carries this fix (teach/Stage.tsx "Chips hug their
              // pill on the canvas; sizing them to the stale stored box here
              // clipped them").
              const autoSize = AUTO_SIZE_TYPES.has(el.type);
              return (
                <div
                  key={el.id}
                  ref={registerElement(el.id)}
                  data-element-id={el.id}
                  // Handwritten text reveals itself character-by-character, so
                  // don't also fade the whole box (that block-fade is what reads
                  // as "it just faded in"). Everything else — including
                  // non-handwritten text — keeps the soft opacity fade.
                  // NOTE: teach/Stage.tsx makes this wrapper a flex box so a
                  // child with no intrinsic height stretches to the authored
                  // box. Do NOT copy that here — `renderBody` already stretches
                  // via `height:100%`, and making this a flex container shrinks
                  // block children (shapes) to zero width, drawing nothing.
                  className={
                    el.type === 'text' && el.handwritten !== false
                      ? 'absolute'
                      : 'absolute transition-opacity duration-300'
                  }
                  style={{
                    left: x,
                    top: y,
                    width: autoSize ? 'auto' : w,
                    height: autoSize ? 'auto' : h,
                    transform: rotation ? `rotate(${rotation}deg)` : undefined,
                    // Rotate about the CENTRE, matching both admin renderers,
                    // which set no transform-origin and therefore get the CSS
                    // default 50% 50% (FreeformElement.tsx:863, teach/Stage.tsx
                    // :278). Pivoting at the top-left instead moved every
                    // rotated element to a different place here than on the
                    // canvas — the drift growing with the element's size and
                    // angle. (The CANVAS div below still pivots top-left: that
                    // one is a scale, where the origin genuinely must be the
                    // coordinate origin.)
                    zIndex: z,
                    opacity: visible ? 1 : 0,
                    pointerEvents: visible ? 'auto' : 'none',
                    // Clip content to the authored box (matches the fixed-frame
                    // editor) so over-long text doesn't spill out and overlap the
                    // elements below it. `customCss` can override via styleOverrides.
                    // Exceptions, all of which paint OUTSIDE their box by design:
                    // images carry their own drop-shadow; callouts rotate/swing
                    // and can carry one; text with accent effects (highlight bar
                    // padding / long shadow) bleeds past its edges; auto-sized
                    // chips/icons have no meaningful box to clip to; and
                    // HANDWRITTEN text shows a pencil that reaches above the top
                    // line — clipping chops the nib off mid-write. The last two
                    // are ported from teach/Stage.tsx, which already fixed them.
                    overflow:
                      autoSize ||
                      el.type === 'image' ||
                      el.type === 'callout' ||
                      (el.type === 'text' && el.handwritten !== false) ||
                      (el.type === 'text' &&
                        !!(el.style?.highlightColor || el.style?.longShadowColor))
                        ? 'visible'
                        : 'hidden',
                    // Authored border / opacity / boxShadow + customCss.
                    ...styleOverrides(el.style),
                  }}
                >
                  {renderBody(el, true, visible)}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  // ─── Region spotlight (Spotlight interface): zoom + vignette ─────────────
  // Mirrors the admin teach Stage: the active spotlight gently zooms + centres
  // its region while a soft vignette caves in; when it ends we keep an "out"
  // phase so the zoom-out + cave-out ease with the SAME duration (otherwise the
  // exit looks rushed).
  const SPOT_EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';
  const activeSp = spotlights[0] ?? null;
  const [spDisplay, setSpDisplay] = useState<{ sp: Spotlight; phase: 'in' | 'out' } | null>(null);
  const spDisplayRef = useRef<{ sp: Spotlight; phase: 'in' | 'out' } | null>(null);
  spDisplayRef.current = spDisplay;
  const spExitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (activeSp) {
      if (spExitTimer.current) clearTimeout(spExitTimer.current);
      spExitTimer.current = null;
      setSpDisplay({ sp: activeSp, phase: 'in' });
      return;
    }
    const cur = spDisplayRef.current;
    if (!cur || cur.phase === 'out') return;
    setSpDisplay({ sp: cur.sp, phase: 'out' });
    spExitTimer.current = setTimeout(() => setSpDisplay(null), (cur.sp.easeMs ?? 700) + 80);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSp?.id, activeSp?.w, activeSp?.h, activeSp?.zoom, activeSp?.easeMs, activeSp?.x, activeSp?.y]);

  const dsp = spDisplay?.sp ?? null;
  const spExiting = spDisplay?.phase === 'out';
  const spEaseMs = dsp?.easeMs ?? 700;
  const spZoomedIn = !!dsp && !spExiting;
  const spZ = spZoomedIn ? dsp.zoom ?? 1.25 : 1;
  // Vignette gradient stays sized to the zoomed region across in/out so it
  // doesn't pop; the cave animation handles the motion.
  const spVz = dsp ? dsp.zoom ?? 1.25 : 1;
  // Region centre in design px → container space is ×scale. The transform is
  // expressed about a TOP-LEFT origin (equivalent math to the admin's region-
  // centred origin) so `measure()`'s un-transform stays exact.
  const spOcx = (dsp ? dsp.x + dsp.w / 2 : CANVAS_WIDTH / 2) * scale;
  const spOcy = (dsp ? dsp.y + dsp.h / 2 : SLIDE_REF_HEIGHT / 2) * scale;
  const spotTransform = spZoomedIn
    ? `translate(${(CANVAS_WIDTH / 2) * scale - spOcx + spOcx * (1 - spZ)}px, ${
        (SLIDE_REF_HEIGHT / 2) * scale - spOcy + spOcy * (1 - spZ)
      }px) scale(${spZ})`
    : 'translate(0px, 0px) scale(1)';

  // ─── Focus cue: zoom transform ────────────────────────────────────────────
  // Two focus kinds share the dim/blur treatment below, but only `zoom` moves
  // the viewport: it zoom/pans so the target box is centred and scaled by
  // `zoomScale`. `spotlight` dims-only (no zoom). The viewport wraps the canvas
  // AND the overlay, so both zoom together. Boxes are in un-zoomed container
  // space (see measure), so the math is direct.
  const isFocus =
    !!spotlight &&
    (spotlight.kind === 'zoom' || spotlight.kind === 'spotlight');
  const isZoom = spotlight?.kind === 'zoom';
  const spotlightBox =
    isFocus && spotlight ? boxes[spotlight.targetElementId] : undefined;

  const viewportTransform = useMemo(() => {
    if (!spotlightBox) return 'translate(0px, 0px) scale(1)';
    const containerW = CANVAS_WIDTH * scale;
    const containerH = scaledHeight;
    if (!isZoom) {
      // `spotlight` kind: a subtle uniform push-in about the screen centre — the
      // whole board zooms slightly (no pan to the element).
      const s = SPOTLIGHT_PUSH_IN;
      const tx = (containerW / 2) * (1 - s);
      const ty = (containerH / 2) * (1 - s);
      return `translate(${tx}px, ${ty}px) scale(${s})`;
    }
    const z = Math.max(1, spotlight?.zoomScale ?? DEFAULT_SPOTLIGHT_ZOOM);
    const cx = spotlightBox.left + spotlightBox.width / 2;
    const cy = spotlightBox.top + spotlightBox.height / 2;
    // Centre (cx, cy) in the container, then clamp so we never pan past the
    // scaled content's edges (no empty gutters around the board).
    const tx = Math.min(0, Math.max(containerW - z * containerW, containerW / 2 - z * cx));
    const ty = Math.min(0, Math.max(containerH - z * containerH, containerH / 2 - z * cy));
    return `translate(${tx}px, ${ty}px) scale(${z})`;
  }, [spotlightBox, isZoom, spotlight, scale, scaledHeight]);

  // ─── Full-window dim + blur behind the spotlight ──────────────────────────
  // While a spotlight is active, a single blurred/darkened layer covers the
  // whole window (portaled to <body>, so it dims the entire app — not just the
  // slide) with a ROUNDED-RECT HOLE cut out around the target via an SVG mask.
  // A separate ring element adds the drop shadow / edge. Both are updated
  // imperatively each frame so the hole tracks the element through the zoom.
  const spotlightTargetId =
    isFocus && spotlight
      ? spotlight.targetElementId
      : null;

  useEffect(() => {
    const dim = dimRef.current;
    const ring = ringRef.current;
    if (!dim) return undefined;
    if (!spotlightTargetId) {
      dim.style.opacity = '0';
      if (ring) ring.style.opacity = '0';
      return undefined;
    }
    let raf = 0;
    // Timestamp the activation so the spotlight can animate its dim closing in.
    const start = performance.now();
    const tick = () => {
      const node = elementRefs.current.get(spotlightTargetId);
      if (node) {
        const r = node.getBoundingClientRect();
        const W = window.innerWidth;
        const H = window.innerHeight;
        if (isZoom) {
          // `zoom` kind: precise rounded-rect hole + blur, with a shadow ring.
          const l = Math.max(0, r.left - SPOTLIGHT_HOLE_PAD);
          const t = Math.max(0, r.top - SPOTLIGHT_HOLE_PAD);
          const w = Math.min(W, r.right + SPOTLIGHT_HOLE_PAD) - l;
          const h = Math.min(H, r.bottom + SPOTLIGHT_HOLE_PAD) - t;
          const rad = SPOTLIGHT_HOLE_RADIUS;
          // Alpha mask: opaque (dim shown) everywhere except the rounded hole,
          // where the inner <mask> punches it transparent → element shows sharp.
          const svg =
            `<svg xmlns='http://www.w3.org/2000/svg' width='${W}' height='${H}'>` +
            `<mask id='m'>` +
            `<rect width='${W}' height='${H}' fill='white'/>` +
            `<rect x='${l}' y='${t}' width='${w}' height='${h}' rx='${rad}' ry='${rad}' fill='black'/>` +
            `</mask>` +
            `<rect width='${W}' height='${H}' fill='white' mask='url(#m)'/>` +
            `</svg>`;
          const url = `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
          dim.style.background = 'rgba(15, 12, 8, 0.45)';
          dim.style.backdropFilter = 'blur(6px)';
          dim.style.maskImage = url;
          dim.style.webkitMaskImage = url;
          dim.style.maskRepeat = 'no-repeat';
          dim.style.webkitMaskRepeat = 'no-repeat';
          if (ring) {
            ring.style.left = `${l}px`;
            ring.style.top = `${t}px`;
            ring.style.width = `${w}px`;
            ring.style.height = `${h}px`;
            ring.style.borderRadius = `${rad}px`;
            ring.style.opacity = '1';
          }
        } else {
          // `spotlight` kind: soft radial "stage light" — bright clear core over
          // the element fading to a dark vignette at the edges (no hole/blur).
          const cx = r.left + r.width / 2;
          const cy = r.top + r.height / 2;
          const targetRx = (r.width / 2 + SPOTLIGHT_SOFT_PAD) * SPOTLIGHT_SOFT_SCALE;
          const targetRy = (r.height / 2 + SPOTLIGHT_SOFT_PAD) * SPOTLIGHT_SOFT_SCALE;
          // Close-in animation: the clear pool starts large enough to cover the
          // whole screen (no dim) and shrinks to the target, so the darkness
          // grows inward from the corners. easeOutCubic for a soft settle.
          const p = Math.min(1, (performance.now() - start) / SPOTLIGHT_CLOSE_MS);
          const e = 1 - (1 - p) ** 3;
          const startR = Math.hypot(W, H) * 3;
          const rx = startR + (targetRx - startR) * e;
          const ry = startR + (targetRy - startR) * e;
          dim.style.maskImage = 'none';
          dim.style.webkitMaskImage = 'none';
          dim.style.backdropFilter = 'none';
          dim.style.background =
            `radial-gradient(ellipse ${rx}px ${ry}px at ${cx}px ${cy}px, ` +
            `rgba(0,0,0,0) 0%, rgba(0,0,0,0) ${SPOTLIGHT_CORE_PCT}%, ` +
            `${SPOTLIGHT_EDGE_DIM} 100%)`;
          if (ring) ring.style.opacity = '0';
        }
        dim.style.opacity = '1';
      }
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [spotlightTargetId, isZoom]);

  return (
    <div
      ref={containerRef}
      // Clip while a focus cue is active (both kinds transform the viewport — the
      // zoom pans, the spotlight pushes in), so the scaled board never spills into
      // the surrounding layout. Un-focused stays un-clipped so edge annotations show.
      className={`relative w-full ${focusCanvas ? 'ruled-paper' : ''} ${
        spotlightBox || spDisplay ? 'overflow-hidden' : ''
      }`}
      style={{
        height: scaledHeight,
        // A canvas is always CANVAS_WIDTH wide, so a short one can only be
        // zoomed until it fills the WIDTH — the leftover above and below is
        // unavoidable. What was wrong is that the authored slide background was
        // painted on the canvas strip only, leaving that leftover as the host's
        // flat white. Carry the background (and the paper surface) across the
        // whole area in focus mode so a coloured slide covers it edge to edge.
        // The scrolling feed is untouched: there the strip always fills the box.
        ...(focusCanvas && slide.background && !isWhiteBackground(slide.background)
          ? { background: slide.background }
          : null),
      }}
    >
      {/*
        Spotlight viewport: identity unless a spotlight is active, then it
        zoom/pans onto the target. Wraps the canvas + overlay so they move as
        one. Transitions smoothly on focus and release. A region spotlight
        (Spotlight interface) takes precedence over the legacy annotation kind.
      */}
      <div
        ref={viewportRef}
        className="absolute inset-0"
        style={{
          transform: spDisplay ? spotTransform : viewportTransform,
          transformOrigin: 'top left',
          transition: spDisplay
            ? `transform ${spEaseMs}ms ${SPOT_EASE}`
            : 'transform 900ms cubic-bezier(0.22, 1, 0.36, 1)',
          willChange: 'transform',
        }}
      >
        {/*
          Native canvas authored against CANVAS_WIDTH; CSS-scaled to fill the
          landscape width. transform-origin top-left keeps coordinates aligned
          with the reserved (scaled) height on the container above.
        */}
        <div
          ref={canvasRef}
          className="stage-canvas ruled-paper absolute left-0 top-0 flex flex-col"
          style={{
            width: CANVAS_WIDTH,
            // Focus mode centres the single fitted canvas in the host box; the
            // scrolling feed keeps its top-left anchor so element coordinates
            // line up with the reserved height above.
            transform: focusCanvas
              ? `translate(${Math.max(0, (containerW - CANVAS_WIDTH * scale) / 2)}px, ${Math.max(
                  0,
                  (scaledHeight - focusBandH * scale) / 2,
                )}px) scale(${scale})`
              : `scale(${scale})`,
            transformOrigin: 'top left',
            // Authored slide background — CSS `background` accepts a gradient or
            // a #hex (BLUEPRINT §13). Applied only when set AND not plain white,
            // so white/default slides keep the themed ruled-paper surface.
            // In focus mode the CONTAINER carries it instead (see above), so it
            // covers the whole area; painting it here too would draw a gradient
            // or image a second time, at a different size.
            ...(!focusCanvas && slide.background && !isWhiteBackground(slide.background)
              ? { background: slide.background }
              : null),
          }}
        >
          {/* Focus mode shows ONE canvas — rendering just that row is what
              guarantees a neighbour can never be half-visible. */}
          {(focusCanvas ? slide.rows.slice(focusRow, focusRow + 1) : slide.rows).map((row) =>
            renderRow(row),
          )}

          {/* Pen/marker strokes — inside the scaled canvas so their design-px
              points land exactly where they were drawn. Each stroke draws on
              (traces start→end) when it mounts. */}
          {drawStrokes.length > 0 && (
            // Explicit viewBox + design-px width/height. WebKit/Safari maps a
            // no-viewBox, percentage-sized inline SVG to a DEFAULT viewport
            // (300×150-derived), not its CSS box — which shifted every stroke off
            // its target in Safari 26.5 (Chromium and Playwright-WebKit rendered it
            // correctly; only real Safari did not). Pinning viewBox="0 0 1280 720"
            // with matching width/height forces a 1:1 design-px coordinate system
            // in every engine; the strokes' points are already in design px.
            <svg
              // FIXED 1280×720 box (NOT h-full/w-full): box === viewBox → a pure
              // 1:1 design-px coordinate identity, independent of the content-driven
              // canvas height and of how each engine resolves a percentage height.
              // This is what makes it render identically in Chromium AND Safari —
              // WebKit/Safari 26.5 mis-mapped the old no-viewBox, percentage-sized
              // SVG to a default viewport and pushed every stroke off-target.
              // overflow:visible so strokes authored below y=720 still show. The
              // parent canvas's transform:scale then scales this with the content.
              className="pointer-events-none absolute left-0 top-0"
              width={CANVAS_WIDTH}
              height={SLIDE_REF_HEIGHT}
              viewBox={`0 0 ${CANVAS_WIDTH} ${SLIDE_REF_HEIGHT}`}
              preserveAspectRatio="xMinYMin meet"
              style={{ zIndex: 30, overflow: 'visible' }}
            >
              {drawStrokes.map((s) => (
                <StrokePath key={s.id} stroke={s} />
              ))}
            </svg>
          )}
        </div>

        <AnnotationOverlay
          annotations={annotations}
          boxes={boxes}
          revealedElementIds={revealedElementIds}
        />
      </div>

      {/* Region-spotlight vignette: a soft black→alpha gradient that leaves the
          focused (centred, zoomed) region clear and fades darkness in toward
          the edges. Oversized + blurred so no circle edge is resolvable; caves
          in/out with the zoom. */}
      {dsp && (
        <div
          // `key` ties the cave animation to the region + phase so entering the
          // exit phase replays it as a cave-OUT.
          key={`${dsp.id}:${spExiting ? 'out' : 'in'}`}
          className="pointer-events-none absolute z-20"
          style={{
            inset: -160,
            filter: 'blur(56px)',
            background: `radial-gradient(ellipse ${dsp.w * spVz * 1.18 * scale}px ${
              dsp.h * spVz * 1.18 * scale
            }px at 50% 50%, rgba(0,0,0,0) 62%, rgba(0,0,0,0.12) 78%, rgba(0,0,0,0.22) 90%, rgba(0,0,0,0.3) 100%)`,
            transformOrigin: '50% 50%',
            animation: `${spExiting ? 'spotlight-cave-out' : 'spotlight-cave-in'} ${spEaseMs}ms ${SPOT_EASE} both`,
          }}
        />
      )}

      {/*
        Full-window focus overlay, portaled to <body> so it sits above (and dims)
        the whole app, not just the slide. pointer-events:none keeps the player
        controls usable. The effect above sets the look per kind each frame:
        `zoom` → blurred rounded-rect hole + ring; `spotlight` → soft radial
        vignette (no hole/blur, ring hidden).
      */}
      {createPortal(
        <>
          <div
            ref={dimRef}
            aria-hidden
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 40,
              opacity: 0,
              pointerEvents: 'none',
              transition: 'opacity 280ms ease, background 200ms ease',
            }}
          />
          {/* Drop shadow + soft edge around the clear hole (no background, so it
              never covers the spotlighted element). */}
          <div
            ref={ringRef}
            aria-hidden
            style={{
              position: 'fixed',
              zIndex: 41,
              opacity: 0,
              pointerEvents: 'none',
              boxShadow:
                '0 12px 40px rgba(0, 0, 0, 0.41), inset 0 0 0 1px rgba(255, 255, 255, 0.18)',
              transition: 'opacity 280ms ease',
            }}
          />
        </>,
        document.body,
      )}
    </div>
  );
}

/** Total polyline length (design px) — sets the dash length for the draw-on. */
function polylineLength(points: { x: number; y: number }[]): number {
  let len = 0;
  for (let i = 1; i < points.length; i++) {
    len += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
  }
  return Math.max(1, len);
}

/** A pen/marker stroke that "draws on" — traces start→end via a dash animation
 *  when it mounts (mirrors the admin teach Stage). Markers (width ≥ 12) render
 *  translucent like a highlighter. */
function StrokePath({ stroke }: { stroke: DrawStroke }) {
  const len = polylineLength(stroke.points);
  const isMarker = stroke.strokeWidth >= 12;
  // Trace speed: ~1px/ms, clamped, slower-feeling for a natural hand.
  const dur = Math.min(1600, Math.max(280, Math.round(len * 0.9)));
  const texture = stroke.texture;
  return (
    <g>
      {texture && <StrokeTextureFilter strokeId={stroke.id} texture={texture} />}
      <polyline
        points={strokePointsAttr(stroke)}
        fill="none"
        stroke={stroke.color}
        strokeWidth={stroke.strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={isMarker ? 0.4 : 1}
        filter={texture ? `url(#${strokeFilterId(stroke.id)})` : undefined}
        style={{
          strokeDasharray: len,
          ['--stroke-len' as string]: String(len),
          animation: `stroke-draw ${dur}ms cubic-bezier(0.25, 0.6, 0.3, 1) both`,
        }}
      />
    </g>
  );
}
